// ===================================
// KAIXO - Configuración de WebSocket
// ===================================

import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://backend-dlmm.onrender.com';

let socket = null;
let socketReadyFired = false;

// ─── Auto-suspend ──────────────────────────────────────────
// Cuando la pestaña/app pasa a oculta y permanece así durante
// SUSPEND_AFTER_MS, desconectamos el socket para liberar recursos
// (memoria + ancho de banda en cliente y servidor). Al volver a
// visible reconectamos automáticamente.
//
// Mientras está suspendido, los mensajes nuevos llegan al usuario
// vía push notification (FCM/APNs/Web Push) en vez de socket en
// tiempo real. Al reconectar, el primer fetch de la lista de
// conversaciones pondrá los badges y mensajes recientes al día.
const SUSPEND_AFTER_MS = 5 * 60 * 1000;
let suspendTimer = null;

function startSuspendCountdown() {
    if (suspendTimer) clearTimeout(suspendTimer);
    suspendTimer = setTimeout(() => {
        if (socket && socket.connected) {
            try { console.log('[socket] suspendiendo por inactividad'); } catch {}
            socket.disconnect();
        }
    }, SUSPEND_AFTER_MS);
}
function cancelSuspendCountdown() {
    if (suspendTimer) { clearTimeout(suspendTimer); suspendTimer = null; }
}
function resumeSocketIfNeeded() {
    if (socket && !socket.connected) {
        try { console.log('[socket] reanudando tras volver a foreground'); } catch {}
        socket.connect();
    }
}

let autoSuspendInstalled = false;
function installAutoSuspendOnce() {
    if (autoSuspendInstalled) return;
    autoSuspendInstalled = true;

    // Web: visibilitychange dispara cuando cambias de pestaña / minimizas.
    if (typeof document !== 'undefined' && document.addEventListener) {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                startSuspendCountdown();
            } else {
                cancelSuspendCountdown();
                resumeSocketIfNeeded();
            }
        });
    }

    // Capacitor nativo: appStateChange. Si el WebView se pausa, los
    // timers JS también — igualmente lanzamos el countdown porque hay
    // navegadores Android que mantienen el WebView vivo en background.
    (async () => {
        try {
            const { App } = await import('@capacitor/app');
            App.addListener('appStateChange', (state) => {
                if (state?.isActive) {
                    cancelSuspendCountdown();
                    resumeSocketIfNeeded();
                } else {
                    startSuspendCountdown();
                }
            });
        } catch {}
    })();
}

export const connectSocket = (userId, onOnline, onOffline, onOnlineList) => {
    if (!socket) {
        const token = localStorage.getItem('kaixo_token') || '';
        socket = io(SOCKET_URL, {
            // Autenticación por JWT en el handshake (el servidor la lee en io.use)
            auth: { token },
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30000,
            randomizationFactor: 0.5,
            timeout: 20000,
            autoConnect: true
        });

        socket.on('connect', () => {
            // Registrar usuario en cada conexión (incluye reconexiones)
            socket.emit('user_connected', userId);
            const isReconnection = socketReadyFired;
            // socket_ready SOLO la primera vez — para evitar listeners duplicados tras reconexión
            if (!socketReadyFired) {
                socketReadyFired = true;
                window.dispatchEvent(new Event('socket_ready'));
            }
            window.dispatchEvent(new CustomEvent('socket_status', { detail: { connected: true } }));
            // Si esto es una RECONEXIÓN (no la primera vez), forzar refresh
            // de conversaciones + grupos. Eventos como `added_to_group` o
            // mensajes nuevos pudieron emitirse mientras estábamos
            // desconectados (suspensión por inactividad, pérdida de red,
            // server bouncing) y se perdieron. Sin esto, el usuario veía
            // su lista vieja hasta hacer algo manual.
            if (isReconnection) {
                window.dispatchEvent(new Event('conversations_updated'));
            }
        });

        socket.on('disconnect', (reason) => {
            window.dispatchEvent(new CustomEvent('socket_status', { detail: { connected: false, reason } }));
            // Si el servidor desconectó intencionalmente, forzar reconexión
            if (reason === 'io server disconnect') {
                socket.connect();
            }
        });

        socket.on('reconnect_attempt', (attempt) => {
            window.dispatchEvent(new CustomEvent('socket_status', { detail: { connected: false, reconnecting: true, attempt } }));
        });

        socket.on('user_online', (data) => {
            if (onOnline) onOnline(data);
        });

        socket.on('user_offline', (data) => {
            if (onOffline) onOffline(data);
        });

        socket.on('online_users', (data) => {
            if (onOnlineList) onOnlineList(data);
        });

        installAutoSuspendOnce();
    }

    return socket;
};

export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket = null;
        socketReadyFired = false;
    }
};

export const getSocket = () => socket;

export const joinConversation = (conversationId) => {
    if (socket) socket.emit('join_conversation', conversationId);
};

export const joinGroup = (groupId) => {
    if (socket) socket.emit('join_group', groupId);
};

export const onNewMessage = (callback) => {
    if (socket) {
        // Evitar duplicados: quitar listeners anteriores con la misma función
        socket.off('new_message', callback);
        socket.on('new_message', callback);
    }
    return () => { if (socket) socket.off('new_message', callback); };
};

export const onMessageConfirmed = (callback) => {
    if (socket) {
        socket.off('message_confirmed', callback);
        socket.on('message_confirmed', callback);
    }
    return () => { if (socket) socket.off('message_confirmed', callback); };
};

export const onNewGroupMessage = (callback) => {
    if (socket) {
        socket.off('new_group_message', callback);
        socket.on('new_group_message', callback);
    }
    return () => { if (socket) socket.off('new_group_message', callback); };
};

export const onMessageReaction = (callback) => {
    if (socket) {
        socket.off('message_reaction');
        socket.on('message_reaction', callback);
    }
};

export const onMessageDeleted = (callback) => {
    if (socket) {
        socket.off('message_deleted');
        socket.on('message_deleted', callback);
    }
};

export const onMessagesRead = (callback) => {
    if (socket) {
        socket.off('messages_read');
        socket.on('messages_read', callback);
    }
};

export const onMessageEdited = (callback) => {
    if (socket) {
        socket.off('message_edited', callback);
        socket.on('message_edited', callback);
    }
    return () => { if (socket) socket.off('message_edited', callback); };
};

// Llega cuando termina la FASE 2 del pipeline de video: el backend ya tiene
// transcripción + subtítulos + traducciones y los emite al cliente para que
// reemplace el indicador "Transcribiendo..." por los subtítulos reales.
export const onMessageTranscriptionReady = (callback) => {
    if (socket) {
        socket.off('message_transcription_ready', callback);
        socket.on('message_transcription_ready', callback);
    }
    return () => { if (socket) socket.off('message_transcription_ready', callback); };
};

export const onUserOnline = (callback) => {
    if (socket) {
        socket.off('user_online', callback);
        socket.on('user_online', callback);
    }
    return () => { if (socket) socket.off('user_online', callback); };
};

export const onUserOffline = (callback) => {
    if (socket) {
        socket.off('user_offline', callback);
        socket.on('user_offline', callback);
    }
    return () => { if (socket) socket.off('user_offline', callback); };
};

export const onMessageRead = (callback) => {
    if (socket) {
        socket.off('message_read', callback);
        socket.on('message_read', callback);
    }
    return () => { if (socket) socket.off('message_read', callback); };
};

export const emitMarkRead = (data) => {
    if (socket) socket.emit('mark_read', data);
};

export const onOnlineUsers = (callback) => {
    if (socket) {
        socket.off('online_users', callback);
        socket.on('online_users', callback);
    }
    return () => { if (socket) socket.off('online_users', callback); };
};

export const onNewGroupMessageGlobal = (callback) => {
    if (socket) {
        socket.off('new_group_message', callback);
        socket.on('new_group_message', callback);
    }
    // Devolvemos cleanup como las demás. Antes faltaba y los listeners
    // se acumulaban en cada montaje de App si pasaba por hot-reload o
    // re-init de socket — múltiples handlers para el mismo evento.
    return () => { if (socket) socket.off('new_group_message', callback); };
};

export const emitTyping = (data) => {
    if (socket) socket.emit('typing', data);
};

export const emitStopTyping = (data) => {
    if (socket) socket.emit('stop_typing', data);
};

export const onUserTyping = (callback) => {
    if (socket) {
        socket.on('user_typing', callback);
    }
    return () => { if (socket) socket.off('user_typing', callback); };
};

export const onUserStopTyping = (callback) => {
    if (socket) {
        socket.on('user_stop_typing', callback);
    }
    return () => { if (socket) socket.off('user_stop_typing', callback); };
};

export const onGroupAvatarUpdated = (callback) => {
    if (socket) {
        socket.off('group_avatar_updated', callback);
        socket.on('group_avatar_updated', callback);
    }
    return () => { if (socket) socket.off('group_avatar_updated', callback); };
};

export const onAddedToGroup = (callback) => {
    if (socket) {
        socket.off('added_to_group', callback);
        socket.on('added_to_group', callback);
    }
    return () => { if (socket) socket.off('added_to_group', callback); };
};

export const onUserProfileUpdated = (callback) => {
    if (socket) {
        socket.off('user_profile_updated', callback);
        socket.on('user_profile_updated', callback);
    }
    return () => { if (socket) socket.off('user_profile_updated', callback); };
};